// MCP Scan API — raw fetch against the Phase A /api/v1/scans surface
// (api/internal/api/rest/handlers_scans.go). These endpoints are NOT in the
// generated openapi schema (they were added out-of-band with the mcpscan
// service), so this module mirrors fleet.ts' raw-fetch + localStorage-token
// pattern rather than the typed openapi-fetch client. The scan detail /
// findings types moved here from the old McpScan.tsx so the page, the detail
// drawer, and the approve modal share one source of truth.
//
//   GET  /scans           → { items: ScanSummary[] }   the governance queue
//   GET  /scans/{id}      → ScanResult                 findings + verdict
//   POST /scans/{id}/approve {policy_id, server_name, command} → ApproveResult
//
// The queue reads `status` ("pending" | "running" | "done" | "error"), not just
// `passed`: an in-flight scan is "Scanning…", not "Failed".

import { newIdempotencyKey } from "@/lib/api/client";

const BASE = import.meta.env.BASE_URL + "api/v1";

function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const tok = localStorage.getItem("bluerock.token");
  return { ...(tok ? { Authorization: `Bearer ${tok}` } : {}), ...(extra ?? {}) };
}

// Appends request_id (also the X-Request-Id response header) when present — 500
// bodies are deliberately generic, so it's the only thing a user can hand
// support to correlate against. Also surfaces the problem `detail` for the
// 422s (SCAN_IS_DRYRUN / SCAN_NOT_PASSED) and 400/404s.
async function explain(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { detail?: string; title?: string; request_id?: string };
    const message =
      typeof body.detail === "string"
        ? body.detail
        : typeof body.title === "string"
          ? body.title
          : `${fallback} (${res.status})`;
    return typeof body.request_id === "string" ? `${message} (ref: ${body.request_id})` : message;
  } catch {
    return `${fallback} (${res.status})`;
  }
}

// ── Stored-scan shapes (parity with api/internal/mcpscan/service.go +
//    api/internal/brtrust). Copied verbatim from the old McpScan.tsx, plus the
//    `status` field the async pipeline now sets. ──────────────────────────────

export interface Evidence {
  path: string;
  start_line: number;
  end_line: number;
  snippet: string;
  match_type: string;
}
export interface RuleFinding {
  rule_id: string;
  rule_name: string;
  rule_version: string;
  finding: string; // VULNERABLE | SECURE | NOT_APPLICABLE
  severity: string; // CRITICAL | HIGH | MEDIUM | LOW
  confidence: string;
  component_name: string;
  evidence: Evidence[] | null;
  rationale: string;
  fix_guidance: string;
  why_this_matters: string;
  vulnerability_details: string;
}
export interface ScoreBlock {
  score: number;
  tier: string;
  capability_band: string;
  blocked_reason: string | null;
}
export interface Artifact {
  artifact_id: string;
  component: { component_name: string; component_path: string; component_type: string };
  component_findings: RuleFinding[] | null;
  repo_findings_inherited: RuleFinding[] | null;
  rollup: {
    component_findings_count?: Record<string, number>;
    worst_case_severity?: string;
  };
  summary: { high_level_risks?: string[] | null; top_remediations?: string[] | null };
  trust_scores: { component_level: ScoreBlock; repo_level: ScoreBlock };
}
export interface Verdict {
  passed: boolean;
  fail_on: string[];
  counts: Record<string, number>;
  worst_severity: string;
  lowest_score: number;
  blocked: boolean;
  blocked_reason?: string;
  failing_findings: RuleFinding[] | null;
}

// The lightweight queue row (GET /scans). `status` is the async lifecycle:
// pending → running → done | error.
export interface ScanSummary {
  scan_id: string;
  principal: string;
  server: string;
  content_hash: string;
  passed: boolean;
  worst_severity: string;
  dry_run: boolean;
  status: string; // pending | running | done | error
  origin: string; // scan | url | import | seed  (only 'scan' is approvable)
  visibility: string; // public | private
  repo_url: string; // public scans: canonical repo URL
  ref: string; // public scans: resolved commit SHA
  vulnerable_count: number;
  created_at: string;
}

// Response from POST /scans/url. `cached` = a prior scan of this repo@ref was
// reused (no new scan). `stale_possible` = the latest-cached fast path returned a
// row that may not be the repo's current HEAD.
export interface PublicScanResponse {
  scan_id: string;
  cached: boolean;
  ref: string;
  repo: string;
  stale_possible?: boolean;
}

// The full stored result (GET /scans/{id}) — adds verdict + artifacts + status.
export interface ScanResult {
  scan_id: string;
  principal: string;
  server: string;
  content_hash: string;
  passed: boolean;
  worst_severity: string;
  dry_run: boolean;
  status: string; // pending | running | done | error
  verdict: Verdict;
  artifacts: Artifact[];
  created_at: string;
}

export interface ApproveRequest {
  policy_id: string;
  server_name: string;
  command: string;
  // Set for a FAILED scan: an explicit admin risk-acceptance. Both are required
  // by the server when the scan did not pass, and are recorded in the audit log.
  override?: boolean;
  justification?: string;
}
export interface ApproveResult {
  approved: boolean;
  override?: boolean;
  policy_id: string;
  new_version: number;
  scan_id: string;
}

export const scansApi = {
  // visibility: "" | "public" | "private" — presentational filter on the queue.
  list: async (visibility = ""): Promise<ScanSummary[]> => {
    const qs = visibility ? `?visibility=${encodeURIComponent(visibility)}` : "";
    const res = await fetch(`${BASE}/scans${qs}`, { cache: "no-store", headers: authHeaders() });
    if (!res.ok) throw new Error(await explain(res, "Could not load scans"));
    const body = (await res.json()) as { items?: ScanSummary[] };
    return body.items ?? [];
  },
  // Scan a PUBLIC git repo (clone-on-green, cache-first). Returns quickly: either a
  // cached hit (cached=true) or an accepted async scan (cached=false, poll the id).
  submitPublicScan: async (
    repo: string,
    opts?: { ref?: string; latestCached?: boolean },
  ): Promise<PublicScanResponse> => {
    const res = await fetch(`${BASE}/scans/url`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json", "Idempotency-Key": newIdempotencyKey() }),
      body: JSON.stringify({ repo, ref: opts?.ref || undefined, latest_cached: opts?.latestCached || undefined }),
    });
    if (!res.ok) throw new Error(await explain(res, "Could not start public repo scan"));
    return (await res.json()) as PublicScanResponse;
  },
  get: async (id: string): Promise<ScanResult> => {
    const res = await fetch(`${BASE}/scans/${encodeURIComponent(id)}`, {
      cache: "no-store",
      headers: authHeaders(),
    });
    if (!res.ok) throw new Error(await explain(res, "Could not load scan detail"));
    return (await res.json()) as ScanResult;
  },
  approve: async (id: string, body: ApproveRequest): Promise<ApproveResult> => {
    const res = await fetch(`${BASE}/scans/${encodeURIComponent(id)}/approve`, {
      method: "POST",
      headers: authHeaders({ "Content-Type": "application/json", "Idempotency-Key": newIdempotencyKey() }),
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(await explain(res, "Could not approve scan"));
    return (await res.json()) as ApproveResult;
  },
};

// ── View helpers shared by the queue + detail drawer ─────────────────────────

// Status rendering config. The WORD carries the meaning (color-blind safety —
// agate DP 3e#8); the caller renders an antd Tag (`tag`) or the neutral Badge
// (`badge`). In-flight and errored scans are rendered honestly — never as a
// false "Failed".
export interface ScanStatusMeta {
  label: string;
  tag?: "processing" | "success" | "error";
  badge?: boolean;
}
export function scanStatusMeta(s: { status: string; passed: boolean; dry_run: boolean }): ScanStatusMeta {
  if (s.status === "pending" || s.status === "running") return { label: "Scanning…", tag: "processing" };
  if (s.status === "error") return { label: "Scan error", tag: "error" };
  if (s.dry_run) return { label: "dry run", badge: true };
  return s.passed ? { label: "Passed", tag: "success" } : { label: "Failed", tag: "error" };
}

// Severity → SeverityText tone (CRITICAL/HIGH hot, MEDIUM warm, LOW/other cool).
export function severityTone(sev: string): "danger" | "warning" | "info" {
  const s = (sev || "").toUpperCase();
  if (s === "CRITICAL" || s === "HIGH") return "danger";
  if (s === "MEDIUM") return "warning";
  return "info";
}

export function fmtWhen(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}
export function relDays(s: string): string {
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return "—";
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
}
export function shortHash(h?: string): string {
  if (!h) return "—";
  const bare = h.replace(/^sha256:/, "");
  return bare.length > 14 ? `${bare.slice(0, 10)}…${bare.slice(-4)}` : bare;
}

// All findings across a result's artifacts (component + inherited repo findings).
export function allFindings(r: ScanResult): RuleFinding[] {
  const out: RuleFinding[] = [];
  for (const a of r.artifacts || []) {
    out.push(...(a.component_findings || []), ...(a.repo_findings_inherited || []));
  }
  return out;
}
